import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useChatStore } from '../store/useChatStore';
import { ChatMessage } from '../types/chat.types';
import useAuthStore from '../store/zustand/auth';

interface UseSocketProps {
  companyId: string;
  enabled?: boolean;
  userId: string;
}

// Global socket instance - shared across all useSocket calls
let globalSocket: Socket | null = null;

// Helper function to get translated text from message
const translateMessage = async (
  message: ChatMessage,
  userLanguage: string
): Promise<ChatMessage> => {
  if (message.originalLanguage === userLanguage) {
    return { ...message, translatedText: message.messageText };
  }

  // Check if translation is already in the message payload
  let translatedText = message.translations?.[userLanguage];

  // If not available yet (backend async), wait briefly and check once more
  if (!translatedText) {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 1500);
    });
    translatedText = message.translations?.[userLanguage];
  }

  return {
    ...message,
    translatedText: translatedText || message.messageText
  };
};

export const useSocket = ({
  userId,
  companyId,
  enabled = true
}: UseSocketProps) => {
  const socketRef = useRef<Socket | null>(null);

  const {
    addMessage,
    updateMessage,
    deleteMessage,
    addTypingUser,
    removeTypingUser,
    setUserOnline,
    setUserOffline,
    incrementUnread,
    currentRoom,
    updateRoomLastMessage
  } = useChatStore();

  useEffect(() => {
    if (!enabled || !userId) return () => {};

    const needsNewSocket = !globalSocket || !globalSocket.connected;

    if (needsNewSocket) {
      if (globalSocket) {
        globalSocket.removeAllListeners();
        globalSocket.disconnect();
      }

      const socketUrl =
        process.env.REACT_APP_BASE_URL || 'http://localhost:3010';
      globalSocket = io(socketUrl, {
        query: { companyId, userId },
        transports: ['websocket']
      });

      if (!globalSocket) return () => {};

      globalSocket.removeAllListeners();

      globalSocket.on('connect', () => {});
      globalSocket.on('disconnect', () => {});

      globalSocket.on('message:received', async (message: ChatMessage) => {
        const preferredLanguage =
          useAuthStore.getState().preferredLanguage || 'en';

        // Don't translate your own messages — show them as typed
        const isMyOwnMessage = message.userId === userId;
        const translatedMessage = isMyOwnMessage
          ? { ...message, translatedText: message.messageText }
          : await translateMessage(message, preferredLanguage);

        addMessage(translatedMessage.chatRoomId, translatedMessage);
        updateRoomLastMessage(translatedMessage.chatRoomId, translatedMessage);

        if (
          currentRoom?.id !== translatedMessage.chatRoomId &&
          !isMyOwnMessage
        ) {
          incrementUnread(translatedMessage.chatRoomId);
        }
      });

      globalSocket.on('message:edited', (message: ChatMessage) => {
        updateMessage(message.chatRoomId, message.id, message);
      });

      globalSocket.on(
        'message:deleted',
        ({ messageId, roomId }: { messageId: string; roomId: string }) => {
          deleteMessage(roomId, messageId);
        }
      );

      globalSocket.on(
        'message:read',
        ({ messageId, userId: readerId, readAt }: any) => {
          const latestMessages = useChatStore.getState().messages;

          Object.keys(latestMessages).forEach((roomId) => {
            const message = latestMessages[roomId]?.find(
              (m) => m.id === messageId
            );
            if (message) {
              const existingReceipts = message.readReceipts || [];
              const alreadyRead = existingReceipts.some(
                (r) => r.userId === readerId
              );

              if (!alreadyRead) {
                updateMessage(roomId, messageId, {
                  readReceipts: [
                    ...existingReceipts,
                    {
                      chatMessageId: messageId,
                      id: `temp-${Date.now()}`,
                      readAt: new Date(readAt),
                      userId: readerId
                    }
                  ]
                });
              }
            }
          });
        }
      );

      globalSocket.on(
        'user:typing',
        ({ userId: typingUserId, userName, roomId }: any) => {
          addTypingUser(roomId, { roomId, userId: typingUserId, userName });
          setTimeout(() => {
            removeTypingUser(roomId, typingUserId);
          }, 3000);
        }
      );

      globalSocket.on(
        'user:stopped-typing',
        ({ userId: typingUserId, roomId }: any) => {
          removeTypingUser(roomId, typingUserId);
        }
      );

      globalSocket.on(
        'user:status',
        ({ userId: statusUserId, status }: any) => {
          if (status === 'online') {
            setUserOnline(statusUserId);
          } else {
            setUserOffline(statusUserId);
          }
        }
      );
    }

    socketRef.current = globalSocket;

    return () => {};
  }, [userId, companyId, enabled]);

  const joinRoom = (roomId: string) => {
    socketRef.current?.emit('room:join', { roomId });
  };

  const leaveRoom = (roomId: string) => {
    socketRef.current?.emit('room:leave', { roomId });
  };

  const sendTypingStart = (roomId: string, userName: string) => {
    socketRef.current?.emit('typing:start', { roomId, userName });
  };

  const sendTypingStop = (roomId: string) => {
    socketRef.current?.emit('typing:stop', { roomId });
  };

  return {
    joinRoom,
    leaveRoom,
    sendTypingStart,
    sendTypingStop,
    socket: socketRef.current
  };
};

export default useSocket;
